import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

import toast from 'react-hot-toast';

const GRAD        = 'linear-gradient(135deg, #090A0B 0%, #001a4d 40%, #004AAD 75%, #0082FB 100%)';
const GRAD_SHADOW = '0 4px 16px rgba(0,74,173,0.35)';

const RefundSupportForm = ({ paymentHistory }) => {
  const [selectedIssueType, setSelectedIssueType] = useState('');
  const [selectedPayment, setSelectedPayment] = useState('');
  const [description, setDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tickets, setTickets] = useState([
    {
      id: 'TKT-001',
      type: 'Payment Issue',
      status: 'In Progress',
      createdAt: '2026-02-10',
      estimatedResolution: '2-3 business days'
    }
  ]);

  const issueTypes = [
    { value: 'refund',          label: 'Request Refund',   icon: 'RotateCcw',    description: 'Request a refund for a payment' },
    { value: 'payment_issue',   label: 'Payment Issue',    icon: 'AlertCircle',  description: 'Report a problem with a payment' },
    { value: 'billing_dispute', label: 'Billing Dispute',  icon: 'FileQuestion', description: 'Dispute a charge or invoice' },
    { value: 'general_support', label: 'General Support',  icon: 'HelpCircle',   description: 'Other billing-related questions' },
  ];

  const handleSubmit = async (e) => {
    e?.preventDefault();
    if (!selectedIssueType || !description) {
      toast?.error('Please fill in all required fields');
      return;
    }

    setIsSubmitting(true);
    setTimeout(() => {
      const newTicket = {
        id: `TKT-${String(tickets?.length + 1)?.padStart(3, '0')}`,
        type: issueTypes?.find(t => t?.value === selectedIssueType)?.label,
        status: 'Open',
        createdAt: new Date()?.toISOString()?.split('T')?.[0],
        estimatedResolution: '2-3 business days'
      };
      setTickets([newTicket, ...tickets]);
      toast?.success('Support ticket created successfully');
      setSelectedIssueType('');
      setSelectedPayment('');
      setDescription('');
      setIsSubmitting(false);
    }, 1000);
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'Open':        { color: 'bg-primary/10 text-primary',           icon: 'Clock'        },
      'In Progress': { color: 'bg-warning/10 text-warning',            icon: 'Loader2'      },
      'Resolved':    { color: 'bg-success/10 text-success',            icon: 'CheckCircle2' },
      'Closed':      { color: 'bg-muted text-muted-foreground',        icon: 'XCircle'      },
    };
    const config = statusConfig?.[status] || statusConfig?.['Open'];
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium ${config?.color} flex items-center gap-1.5 w-fit`}>
        <Icon name={config?.icon} size={12} />
        {status}
      </span>
    );
  };

  const isSubmitDisabled = !selectedIssueType || !description || isSubmitting;

  return (
    <div className="space-y-6">
      {/* Support Request Form */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <Icon name="MessageSquare" size={20} />
            Submit Support Request
          </h2>
          <p className="text-sm text-muted-foreground mt-1">Get help with billing issues, refunds, or payment disputes</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Issue Type Selection */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-3">Issue Type *</label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {issueTypes?.map((type) => {
                const isSelected = selectedIssueType === type?.value;
                return (
                  <button
                    key={type?.value}
                    type="button"
                    onClick={() => setSelectedIssueType(type?.value)}
                    style={isSelected ? {
                      display: 'flex', alignItems: 'flex-start', gap: 12,
                      padding: 16, borderRadius: 8, textAlign: 'left',
                      border: '2px solid transparent',
                      background: `linear-gradient(var(--card, #fff), var(--card, #fff)) padding-box, ${GRAD} border-box`,
                      cursor: 'pointer', width: '100%', fontFamily: 'inherit',
                      transition: 'all 0.15s',
                    } : {
                      display: 'flex', alignItems: 'flex-start', gap: 12,
                      padding: 16, borderRadius: 8, textAlign: 'left',
                      border: '1px solid var(--border, #e2e8f0)',
                      background: 'transparent',
                      cursor: 'pointer', width: '100%', fontFamily: 'inherit',
                      transition: 'all 0.15s',
                    }}
                  >
                    {/* Icon circle */}
                    <div style={{
                      flexShrink: 0,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      width: 40, height: 40, borderRadius: 8,
                      background: isSelected
                        ? GRAD
                        : 'var(--muted, #f8fafc)',
                    }}>
                      <Icon
                        name={type?.icon}
                        size={20}
                        color={isSelected ? '#ffffff' : 'var(--muted-foreground, #94a3b8)'}
                      />
                    </div>
                    <div style={{ flex: 1 }}>
                      <p style={{
                        fontFamily: 'inherit', fontSize: 14, fontWeight: 600, margin: '0 0 3px',
                        color: 'var(--foreground, #0f172a)',
                      }}>{type?.label}</p>
                      <p style={{
                        fontFamily: 'inherit', fontSize: 12, margin: 0,
                        color: 'var(--muted-foreground, #64748b)',
                      }}>{type?.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Payment Selection (for refund/dispute) */}
          {(selectedIssueType === 'refund' || selectedIssueType === 'billing_dispute') && (
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">Select Payment</label>
              <select
                value={selectedPayment}
                onChange={(e) => setSelectedPayment(e?.target?.value)}
                className="w-full px-4 py-2 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                <option value="">Choose a payment...</option>
                {paymentHistory?.filter(p => p?.payment_status === 'captured')?.map((payment) => (
                  <option key={payment?.id} value={payment?.id}>
                    {new Date(payment?.paid_at)?.toLocaleDateString()} - {payment?.currency === 'INR' ? '₹' : '$'}{(payment?.amount / 100)?.toFixed(2)} ({payment?.razorpay_payment_id?.slice(0, 15)})
                  </option>
                ))}
              </select>
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-foreground mb-2">Description *</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e?.target?.value)}
              placeholder="Please describe your issue in detail..."
              rows={6}
              className="w-full px-4 py-3 bg-background border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 resize-none"
            />
          </div>

          {/* Submit Button */}
          <div className="flex items-center gap-3">
            <button
              type="submit"
              disabled={isSubmitDisabled}
              style={{
                display: 'inline-flex', alignItems: 'center', gap: 7,
                padding: '10px 22px', borderRadius: 100,
                border: 'none', cursor: isSubmitDisabled ? 'not-allowed' : 'pointer',
                background: isSubmitDisabled ? '#e2e8f0' : GRAD,
                color: isSubmitDisabled ? '#94a3b8' : '#fff',
                fontSize: 14, fontWeight: 700, fontFamily: 'inherit',
                boxShadow: isSubmitDisabled ? 'none' : GRAD_SHADOW,
                transition: 'filter 0.15s, transform 0.15s',
                opacity: isSubmitDisabled ? 0.7 : 1,
              }}
              onMouseEnter={e => { if (!isSubmitDisabled) { e.currentTarget.style.filter = 'brightness(1.08)'; e.currentTarget.style.transform = 'translateY(-1px)'; } }}
              onMouseLeave={e => { e.currentTarget.style.filter = ''; e.currentTarget.style.transform = ''; }}
            >
              {isSubmitting ? (
                <>
                  <Icon name="Loader2" size={15} color="#fff" className="animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Icon name="Send" size={15} color={isSubmitDisabled ? '#94a3b8' : '#fff'} />
                  Submit Request
                </>
              )}
            </button>
            <p className="text-xs text-muted-foreground">Estimated response time: 24-48 hours</p>
          </div>
        </form>
      </div>

      {/* Support Tickets */}
      <div className="bg-card border border-border rounded-xl p-6">
        <div className="mb-6">
          <h2 className="text-xl font-semibold text-foreground flex items-center gap-2">
            <Icon name="Ticket" size={20} />
            Your Support Tickets
          </h2>
          <p className="text-sm text-muted-foreground mt-1">Track the status of your support requests</p>
        </div>

        {tickets?.length > 0 ? (
          <div className="space-y-4">
            {tickets?.map((ticket) => (
              <div key={ticket?.id} className="bg-muted/30 rounded-lg p-4 border border-border">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <p className="font-medium text-foreground">{ticket?.type}</p>
                      <span className="text-xs text-muted-foreground">#{ticket?.id}</span>
                    </div>
                    <p className="text-xs text-muted-foreground">Created on {new Date(ticket?.createdAt)?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</p>
                  </div>
                  {getStatusBadge(ticket?.status)}
                </div>
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Icon name="Clock" size={14} />
                  <span>Estimated resolution: {ticket?.estimatedResolution}</span>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12">
            <Icon name="Ticket" size={48} className="mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground">No support tickets yet</p>
          </div>
        )}
      </div>

      {/* Contact Info */}
      <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-primary mt-0.5" />
          <div>
            <p className="font-medium text-foreground mb-2">Need Immediate Assistance?</p>
            <p className="text-sm text-muted-foreground mb-3">For urgent billing issues, you can contact our support team directly:</p>
            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2 text-foreground">
                <Icon name="Mail" size={14} className="text-primary" />
                <span>billing@mydeckaudit.com</span>
              </div>
              <div className="flex items-center gap-2 text-foreground">
                <Icon name="Phone" size={14} className="text-primary" />
                <span>+1 (555) 123-4567</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RefundSupportForm;